import { useState } from 'react';
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ProcessorEditHeader } from '../../components/processor-edit-header';

import { ProcessorDetails, useProcessorById, useUpdateProcessor } from '@/lib/api/processors';
import { ProcessorDryModeField } from '../../components/processor-dry-mode-field';
import { ErrorDisplay } from '@/components/ui/error-display';
import { parseTRPCError } from '@/lib/utils/trpc-error-utils';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { SecretInput } from '@/components/form/secret-input';
import { useProcessorEditId } from '@/app/processors/hooks/use-processor-edit-id';
import { useProcessorEditExit } from '@/app/processors/hooks/use-processor-edit-exit';

interface TrustFlowEditPageProps {
  processor: ProcessorDetails;
}

const TrustFlowEditPageContainerInner = () => {
  const { t } = useTranslation('processors');
  const id = useProcessorEditId();
  const { data, isLoading, error } = useProcessorById(id!);

  if (isLoading) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return <ErrorDisplay error={parseTRPCError(error)} />;
  }

  if (!data) {
    return <div className="container mx-auto py-8">{t('processorNotFound')}</div>;
  }

  return <TrustFlowEditPage processor={data} />;
};

const TrustFlowEditPage = ({ processor }: TrustFlowEditPageProps) => {
  const { t } = useTranslation('processors');
  const navigate = useNavigate();
  const processorExit = useProcessorEditExit();
  const { toast } = useToast();

  const [validationError, setValidationError] = useState<string | null>(null);

  const updateProcessorMutation = useUpdateProcessor();

  const [formData, setFormData] = useState({
    name: processor.name || '',
    enabled: processor.enabled ?? true,
    testMode: (processor.options as any)?.testMode ?? false,
    dryMode: (processor.options as any)?.dryMode === true,
    appId: (processor.options as any)?.appId || '',
    secretKey: (processor.options as any)?.secretKey || '',
  });

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.appId) {
      setValidationError(t('trustflow.credentials.appIdRequired'));
      return;
    }

    if (!formData.secretKey) {
      setValidationError(t('trustflow.credentials.secretKeyRequired'));
      return;
    }

    try {
      await updateProcessorMutation.mutateAsync({
        processorId: processor.id,
        updatedData: {
          type: 'trustflow',
          name: formData.name,
          enabled: formData.enabled,
          options: {
            testMode: formData.testMode,
            dryMode: formData.dryMode,
            appId: formData.appId,
            secretKey: formData.secretKey,
          },
        } as any,
      });

      toast({ description: t('processorUpdated') });
      if (processorExit.isEmbed) {
        processorExit.exitAfterSave();
        return;
      }
      navigate(-1);
    } catch (error) {
      console.error('Error saving processor:', error);
      toast({ description: t('failedToSave'), variant: 'destructive' });
    }
  };

  return (
    <div className="container mx-auto py-8">
      <ProcessorEditHeader
        processorId={processor.id}
        title={t('trustflow.editTitle')}
        onCancel={() => (processorExit.isEmbed ? processorExit.exitCancel() : navigate(-1))}
        onSave={handleSubmit}
        isSaving={updateProcessorMutation.isPending}
      />

      {validationError && (
        <Alert variant="destructive" className="mb-6">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        {/* General Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('generalSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">{t('name')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('enterProcessorName')}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="enabled"
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked === true })}
              />
              <Label htmlFor="enabled">{t('enabled')}</Label>
            </div>
          </CardContent>
        </Card>

        {/* Test Mode Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('testModeSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="testMode"
                checked={formData.testMode}
                onCheckedChange={(checked) => setFormData({ ...formData, testMode: checked === true })}
              />
              <Label htmlFor="testMode">{t('enableTestMode')}</Label>
            </div>
            {formData.testMode && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                <p className="text-sm text-yellow-800">
                  {t('trustflow.testMode.description')}
                </p>
                <div className="mt-2 space-y-1 text-sm text-yellow-700">
                  <p>
                    <strong>{t('trustflow.testMode.testCardsTitle')}</strong>
                  </p>
                  <p>• {t('trustflow.testMode.visaCard')}</p>
                  <p>• {t('trustflow.testMode.mastercardCard')}</p>
                  <p>• {t('trustflow.testMode.threeDsOtp')}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* TrustFlow Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('trustflow.credentials.appIdLabel')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="appId">{t('trustflow.credentials.appIdLabel')}</Label>
              <Input
                id="appId"
                value={formData.appId}
                onChange={(value) => setFormData({ ...formData, appId: value.target.value })}
                placeholder={t('trustflow.credentials.appIdPlaceholder')}
              />
              <p className="text-muted-foreground mt-1 text-xs">{t('trustflow.credentials.appIdDescription')}</p>
            </div>

            <SecretInput
              label={t('trustflow.credentials.secretKeyLabel')}
              value={formData.secretKey}
              onChange={(value) => setFormData({ ...formData, secretKey: value })}
              placeholder={t('trustflow.credentials.secretKeyPlaceholder')}
            />
            <p className="text-muted-foreground mt-1 text-xs">
              {t('trustflow.credentials.secretKeyDescription')}
            </p>
          </CardContent>
        </Card>

                <ProcessorDryModeField
                    checked={formData.dryMode}
                    onCheckedChange={(checked) => setFormData({ ...formData, dryMode: checked })}
                    disabled={updateProcessorMutation.isPending}
                />
      </div>
    </div>
  );
};

export const TrustFlowEditPageContainer = withRequiredPermission(TrustFlowEditPageContainerInner, PERMISSIONS.PROCESSORS_MANAGE);
